import { Link } from "wouter";
import { Footer } from "../components/Footer";
import type { LegalDocumentSlug } from "./types";
import { useLegalDocuments } from "./useLegalDocuments";
import styles from "./LegalPage.module.css";

const AFVENTER_MARKOER = "[UDFYLDES FØR GODKENDELSE]";

function formatDato(value: string): string {
  const dato = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(dato.getTime())) return value;
  return dato.toLocaleDateString("da-DK", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function LegalParagraph({ text }: { text: string }) {
  const markoer = text.indexOf(AFVENTER_MARKOER);
  if (markoer === -1) return <p>{text}</p>;

  const afklaret = text.slice(0, markoer).trim();
  const afventer = text.slice(markoer + AFVENTER_MARKOER.length).trim();
  return (
    <>
      {afklaret.length > 0 && <p>{afklaret}</p>}
      <div className={styles.pending}>
        <strong>Afventer juridisk afklaring</strong>
        <p>{afventer}</p>
      </div>
    </>
  );
}

export function LegalPage({ slug }: { slug: LegalDocumentSlug }) {
  const { bundle, error, retry } = useLegalDocuments();
  const document = bundle?.documents.find((item) => item.slug === slug);

  return (
    <div className={styles.page}>
      <main className={styles.document}>
        <nav className={styles.topNav} aria-label="Juridisk dokument">
          <Link href="/signup" className={styles.brand}>
            app<span>smeden</span>
          </Link>
          <Link href="/" className={styles.backLink}>Tilbage til appen</Link>
        </nav>
        {error && (
          <div className={styles.error} role="alert">
            <p>Dokumentet kunne ikke hentes.</p>
            <button type="button" onClick={retry}>Prøv igen</button>
          </div>
        )}
        {bundle === null && !error && <p>Henter dokument…</p>}
        {bundle !== null && document === undefined && (
          <p className={styles.error} role="alert">
            Dokumentet findes ikke i den aktuelle dokumentpakke.
          </p>
        )}
        {bundle !== null && document !== undefined && (
          <article>
            <header className={styles.header}>
              <p className={styles.badge}>Under juridisk godkendelse</p>
              <h1>{document.title}</h1>
              <p className={styles.meta}>
                Version {document.version} · Opdateret {formatDato(bundle.updatedAt)}
              </p>
              <p className={styles.gate}>
                Dokumentet er en kladde og er endnu ikke juridisk godkendt.
              </p>
            </header>
            <div className={styles.sections}>
              {document.sections.map((section) => (
                <section key={section.heading}>
                  <h2>{section.heading}</h2>
                  {section.paragraphs.map((paragraph) => (
                    <LegalParagraph key={paragraph} text={paragraph} />
                  ))}
                </section>
              ))}
            </div>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
